import {
  Component,
  computed,
  effect,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { ModalBaseComponent } from '../../../shared/components/modal-base/modal-base.component';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { FormValidationService } from '../../../core/services/form-validation.service';
import { firstValueFrom, Observable, of, startWith } from 'rxjs';
import { Store } from '@ngrx/store';
import * as fromStore from '../../../core/store/reducers';
import { BookingsService } from '../../../core/services/bookings.service';
import { Booking } from '../../../core/models/entities/booking';
import { BookableBy } from '../../../core/models/common/bookable-by';
import { formatISO, subDays } from 'date-fns';
import {
  ModalInstance,
  ModalService,
} from '../../../core/services/modal.service';
import { ToastService } from '../../../core/services/toast.service';
import { RecurrenceFormComponent } from '../../../shared/components/recurrence-form/recurrence-form.component';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import {
  UpdateRecurrenceModalComponent,
  UpdateRecurrenceType,
} from '../../../shared/components/update-recurrence-modal/update-recurrence-modal.component';
import { BookingInvite } from '../../../core/models/entities/booking-invite';
import {
  UserOrEmail,
  UsersAutocompleteListComponent,
} from '../../../shared-manager/components/users-autocomplete-list/users-autocomplete-list.component';
import { bookingsActions } from '../../../core/store/bookings';
import {
  BookingDurationType,
  BookingsScope,
} from '../../../core/store/bookings/bookings.state';
import { fadeAnimation } from '../../../core/animations/fade.animation';
import { toSignal } from '@angular/core/rxjs-interop';
import { BookingDateSelectionComponent } from '../../../shared/components/booking-date-selection/booking-date-selection.component';
import { BookingHelper } from '../../../core/helpers/booking.helper';
import { BookingHourSelectionComponent } from '../../../shared/components/booking-hour-selection/booking-hour-selection.component';
import { DatetimeHelper } from '../../../core/helpers/datetime.helper';
import { RoomHelper } from '../../../core/helpers/room.helper';
import { take } from 'rxjs/operators';
import { ApiError } from '../../../core/models/common/api-error';

enum Tab {
  Booking,
  Invites,
}

@Component({
  selector: 'app-create-booking-modal',
  animations: [fadeAnimation],
  template: `
    <app-modal [modalInstance]="modalInstance" (onOk)="onSubmit()">
      <div class="mb-8 grid grid-cols-2 font-medium lg:hidden">
        <div
          class="cursor-pointer border-b pb-2"
          [ngClass]="{
            'border-secondary-500 text-secondary-500':
              currentMobileTab === Tab.Booking,
            'border-gray-muted text-gray-muted':
              currentMobileTab !== Tab.Booking,
          }"
          (click)="currentMobileTab = Tab.Booking"
          >
          Événement
        </div>
        <div
          class="cursor-pointer border-b pb-2"
          [ngClass]="{
            'border-secondary-500 text-secondary-500':
              currentMobileTab === Tab.Invites,
            'border-gray-muted text-gray-muted':
              currentMobileTab !== Tab.Invites,
          }"
          (click)="currentMobileTab = Tab.Invites"
          >
          Invités
        </div>
      </div>
      <div class="grid grid-cols-1 lg:grid-cols-2">
        <div
          class="lg:block lg:pr-12"
          [class.hidden]="currentMobileTab === Tab.Invites"
          >
          @if (form) {
            <form>
              <app-text-input
                [control]="form.controls.name"
                name="name"
                label="Intitulé de la réservation"
              ></app-text-input>
              <app-select-input
                [control]="form.controls.room_id"
                name="room_id"
                label="Salle à réserver"
                [options]="roomsOptions()"
              ></app-select-input>
              <app-booking-date-selection
                [rooms]="selectedRoom() ? [selectedRoom()] : []"
                [booking]="booking"
                [displayAllDates]="true"
                [calendarAlwaysVisible]="false"
                [privateBooking]="true"
                />
              @if (
                dateSelectionComponent().durationType() ===
                BookingDurationType.Hourly &&
                dateSelectionComponent().form.value.dates[0] &&
                roomId()
                ) {
                <app-booking-hour-selection
                  class="mb-6 block"
                  [rooms]="[selectedRoom()]"
                  [date]="dateSelectionComponent().form.value.dates[0]"
                  [displayPastTimes]="true"
                  [editingBooking]="booking"
                  [privateBooking]="true"
                  />
              }
            </form>
          }
          @if (!hasRecurrence()) {
            <app-button
              (click)="hasRecurrence.set(true)"
              variant="outlined"
              size="sm"
              [attr.data-cy]="'add-recurrence-button'"
              >Ajouter une récurrence
            </app-button>
          }
          @if (hasRecurrence()) {
            <div>
              <div class="border-secondary-500 bg-light mb-4 border p-4">
                <app-recurrence-form
                  #recurrenceForm
                  [recurrence]="booking?.recurrence"
                  [bookingStart]="formatISO(startDatetime)"
                  [bookingEnd]="formatISO(endDatetime)"
                  [bookingId]="booking?.id"
                  [roomId]="selectedRoom()?.id"
                  [allDay]="dateSelectionComponent()?.allDay()"
                ></app-recurrence-form>
              </div>
              <button
                (click)="hasRecurrence.set(false)"
                data-cy="delete-recurrence-button"
                ><i class="fal fa-trash mr-2"></i>Supprimer la récurrence
              </button>
            </div>
          }
          <div class="mt-8">
            <app-rich-text-input
              [control]="form.get('comment')"
              name="comment"
              label="Note interne"
            ></app-rich-text-input>
          </div>
          <app-custom-fields-inputs
            [visibility]="BookableBy.Private"
            [room]="selectedRoom()"
            [formArray]="$any(form.get('custom_fields'))"
            [customFieldsToEdit]="booking?.custom_fields ?? null"
            [editingBookingId]="booking?.id ?? null"
          ></app-custom-fields-inputs>
        </div>
    
        <div
          class="border-gray-muted lg:block lg:border-l lg:pl-12"
          [class.hidden]="currentMobileTab === Tab.Booking"
          >
          <div class="text-xl font-medium uppercase">Ajouter des invités</div>
          <app-users-autocomplete-list
            [usersOrEmails$]="invites$"
            (deleteUserOrEmail)="deleteInvite($event)"
          ></app-users-autocomplete-list>
          <div class="mt-10">
            <app-textarea-input
              [control]="form.get('invites_comment')"
              name="comment"
              label="Message d'invitation"
            ></app-textarea-input>
          </div>
          <app-single-checkbox-input
            label="Notifier les invités"
            [control]="form.get('notify_invites')"
            name="notify_invites"
          ></app-single-checkbox-input>
        </div>
      </div>
    </app-modal>
    `,
  standalone: false,
})
export class PrivateBookingModalComponent
  extends ModalBaseComponent
  implements OnInit
{
  bookingSaved = output<void>();
  booking: Booking; // à passer en paramètre en cas d'édition
  protected dateSelectionComponent = viewChild(BookingDateSelectionComponent);
  protected hourSelectionComponent = viewChild(BookingHourSelectionComponent);
  protected recurrenceForm = viewChild(RecurrenceFormComponent);
  protected invitesFormComponent = viewChild(UsersAutocompleteListComponent);
  protected rooms = this.store.selectSignal(
    fromStore.selectAllRoomsBookableBy(BookableBy.Private),
  );
  protected roomsOptions = computed(() =>
    this.rooms().map((room) => ({ label: room.name, value: room.id })),
  );
  protected readonly BookingDurationType = BookingDurationType;
  protected readonly formatISO = formatISO;
  protected sendingData: boolean;
  protected form = new FormGroup({
    name: new FormControl('', [Validators.required]),
    room_id: new FormControl(null, [Validators.required]),
    comment: new FormControl(null),
    invites_comment: new FormControl(null),
    notify_invites: new FormControl(false),
    custom_fields: new FormArray([]),
  });
  protected roomId = toSignal(
    this.form.controls.room_id.valueChanges.pipe(startWith(null)),
  );
  protected selectedRoom = computed(() =>
    this.rooms().find((room) => room.id == this.roomId()),
  );
  protected hasRecurrence = signal(false);
  protected updateRecurrenceType: UpdateRecurrenceType | null;

  protected Tab = Tab;
  protected currentMobileTab = Tab.Booking;

  constructor(
    private bookingsService: BookingsService,
    private formValidationService: FormValidationService,
    private toastService: ToastService,
    private store: Store,
    private modalService: ModalService,
  ) {
    super();

    effect(() => {
      if (this.roomsOptions().length === 1 && this.roomId() === null) {
        this.form.controls.room_id.patchValue(this.roomsOptions()[0].value);
      }
    });
  }

  ngOnInit() {
    this.form.patchValue({
      name: this.booking?.name ?? '',
      room_id: this.booking?.room_id ?? null,
      comment: this.booking?.comment ?? null,
      invites_comment: this.booking?.invites_comment ?? null,
      notify_invites: this.booking?.notify_invites ?? false,
    });

    this.hasRecurrence.set(!!this.booking?.recurrence);
    if (this.booking) this.setFormData();
  }

  setFormData() {
    const end: Date = subDays(
      new Date(this.booking.end),
      this.booking.rooms.some((room) => room.max_end_next_day) ? 1 : 0,
    );
    this.form.patchValue({
      name: this.booking.name,
      comment: this.booking.comment,
      invites_comment: this.booking.invites_comment,
      notify_invites: this.booking.notify_invites,
      room_id: this.booking.room_id,
    });
  }

  get invites$(): Observable<BookingInvite[]> {
    return of(this.booking?.booking_invites ?? []);
  }

  openIgnoreConflictsConfirmationModal(dates: string[]) {
    let message =
      '<div>La salle n’est pas disponible à certaines dates.<br/> ' +
      '<ul class="mb-4">';
    for (const date of dates) {
      message += `<li>${date}</li>`;
    }
    message +=
      '</ul>La récurrence ne sera pas ajoutée à cette (ces) date(s).' +
      '<div class="font-medium mt-4">Êtes-vous sûr·e de vouloir ajouter la récurrence ?</div></div>';

    const instance = this.modalService.open(ConfirmModalComponent, {
      title: 'Salle non disponible',
      message,
      okAction: {
        title: 'Oui',
      },
      cancelAction: {
        title: 'Non',
      },
    });
    instance.okClicked$.subscribe(() => {
      this.onSubmit(true);
      this.modalService.close();
    });
  }

  deleteInvite(userOrEmail: UserOrEmail) {
    const modalInstance: ModalInstance = this.modalService.open(
      ConfirmModalComponent,
      {
        title: 'Supprimer une personne invitée',
        message: `Êtes-vous sûr·e de vouloir supprimer l'invitation adressée à ${userOrEmail.email} ?`,
        okAction: {
          title: 'Oui',
        },
        cancelAction: {
          title: 'Non',
        },
      },
    );
    modalInstance.okClicked$.subscribe(async () => {
      modalInstance.saving = true;
      try {
        const invite = this.booking.booking_invites.find(
          (invite) => invite.email === userOrEmail.email,
        );
        const booking = await this.bookingsService.deleteInvite(
          this.booking.id,
          invite.id,
        );
        this.store.dispatch(bookingsActions.setFocusedBooking({ booking }));
        this.store.dispatch(
          bookingsActions.deleteInvite({
            scope: BookingsScope.All,
            booking: this.booking,
            invite,
          }),
        );
        this.modalService.close();
        modalInstance.saving = false;
      } catch (exception: any) {
        this.toastService.error(exception.message);
        modalInstance.saving = false;
      }
    });
  }

  get startDatetime() {
    return BookingHelper.getStartDatetime(
      this.dateSelectionComponent()?.allDay(),
      this.dateSelectionComponent()?.startDate(),
      this.hourSelectionComponent()?.startTime(),
    );
  }

  get endDatetime() {
    return BookingHelper.getEndDatetime(
      this.dateSelectionComponent()?.allDay(),
      this.dateSelectionComponent()?.startDate(),
      this.hourSelectionComponent()?.startTime(),
      this.dateSelectionComponent()?.endDate(),
      this.hourSelectionComponent()?.endTime(),
    );
  }

  async onSubmit(ignore_recurrence_conflicts = null) {
    if (this.modalInstance.saving) return;

    if (!ignore_recurrence_conflicts) {
      this.updateRecurrenceType = null;
    }
    if (
      this.formValidationService.validate(this.form) &&
      (!this.hasRecurrence() ||
        this.formValidationService.validate(this.recurrenceForm()?.form))
    ) {
      if (
        this.booking?.recurrence &&
        this.hasRecurrence() &&
        !ignore_recurrence_conflicts
      ) {
        const instance = this.modalService.open(
          UpdateRecurrenceModalComponent,
          {},
        );
        const component = instance.componentRef
          .instance as UpdateRecurrenceModalComponent;
        component.originalBooking = this.booking;
        component.updatedBooking = {
          start: DatetimeHelper.dateToApiFormat(this.startDatetime, true),
          end: DatetimeHelper.dateToApiFormat(this.endDatetime, true),
          recurrence: {
            ...this.recurrenceForm()?.value,
            end: formatISO(
              RoomHelper.getEndAccordingToRoomMaxEnd(
                this.selectedRoom(),
                this.recurrenceForm()?.value.end,
                this.dateSelectionComponent().allDay(),
              ),
            ),
          },
        } as Booking;
        const value = await firstValueFrom(component.onClosed.pipe(take(1)));
        if (!value) return;
        this.modalService.close();
        this.updateRecurrenceType = value;
      }

      try {
        this.modalInstance.saving = true;
        const data = {
          id: this.booking?.id,
          name: this.form.value.name,
          comment: this.form.value.comment,
          start: DatetimeHelper.dateToApiFormat(this.startDatetime, true),
          end: DatetimeHelper.dateToApiFormat(this.endDatetime, true),
          room_id: this.form.value.room_id,
          is_public: false,
          all_day: this.dateSelectionComponent().allDay(),
          recurrence: this.hasRecurrence()
            ? this.recurrenceForm()?.value
            : null,
          ignore_recurrence_conflicts,
          update_recurrence_type: this.updateRecurrenceType,
          booking_invites: this.invitesFormComponent().validResults,
          invites_comment: this.form.value.invites_comment,
          notify_invites: this.form.value.notify_invites,
          custom_fields: this.form.value.custom_fields ?? null,
        };

        let booking: Booking;
        if (this.booking) {
          booking = await this.bookingsService.update(
            this.booking.id,
            // @ts-ignore
            data,
          );
        } else {
          booking = await this.bookingsService.storePrivate(data);
        }
        this.toastService.success(
          (this.booking
            ? 'Réservation modifiée : '
            : 'Réservation ajoutée : ') + booking.name,
        );
        this.store.dispatch(bookingsActions.setFocusedBooking({ booking }));
        this.bookingSaved.emit();
        this.modalService.close();
      } catch (exception) {
        console.error(exception);
        if ((exception as ApiError).errors['dates_conflicts']) {
          this.openIgnoreConflictsConfirmationModal(
            (exception as ApiError).errors['dates_conflicts'],
          );
        } else {
          this.formValidationService.displayApiValidationErrors(
            this.form,
            exception as ApiError,
          );
        }
      } finally {
        this.modalInstance.saving = false;
      }
    }
  }

  protected readonly BookableBy = BookableBy;
}
